import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  FaShareAlt,
  FaRegCalendarAlt,
  FaDollarSign,
  FaArrowLeft,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { testUrl } from "../BaseData";
import classes from "./GrantlarVaLoyihalar.module.css";
import GrantCard from "./GrantCard";
import {
  getLabels,
  normLang,
  pick,
  formatDate,
  daysLeft,
  flagUrl,
  imgUrl,
  fixHtml,
  shareLink,
} from "./grantUtils";

const GrantDetail = () => {
  const { id } = useParams();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const L = getLabels(lang);

  const [grant, setGrant] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${testUrl}/api/grants/${id}/`)
      .then((res) => {
        setGrant(res.data.grant || null);
        setRelated(res.data.related || []);
      })
      .catch((err) =>
        setError(err?.response?.status === 404 ? L.notFound : L.error)
      )
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  if (loading) return <div className={classes.status}>{L.loading}</div>;
  if (error) return <div className={classes.status}>{error}</div>;
  if (!grant) return <div className={classes.status}>{L.notFound}</div>;

  const title = pick(grant, "title", lang);
  const country = pick(grant, "country", lang);
  const funding = pick(grant, "funding", lang);
  const content = pick(grant, "content", lang);
  const flag = flagUrl(grant.country_code);
  const left = daysLeft(grant.deadline);
  const expired = left !== null && left < 0;

  let deadlineNote = null;
  if (left === null) deadlineNote = null;
  else if (left < 0) deadlineNote = L.expired;
  else if (left === 0) deadlineNote = L.lastDay;
  else deadlineNote = L.daysLeft(left);

  const handleShare = async () => {
    const url = `${window.location.origin}/${normLang(lang)}/grantlar/${grant.id}`;
    const result = await shareLink(url, title);
    if (result === "copied") setToast(L.copied);
  };

  const audienceLabel =
    grant.audience === "all"
      ? `${L.students} · ${L.teachers}`
      : L[grant.audience] || "";
  const scopeLabel = L[grant.scope] || "";

  return (
    <div className={classes.detail}>
      <Link to="/activity/ilmiy-faoliyat/grantlar-va-loyihalar" className={classes.back}>
        <FaArrowLeft /> {L.back}
      </Link>

      <div className={classes.detailCard}>
        <div className={classes.detailImageWrap}>
          <img src={imgUrl(grant.image)} alt={title} className={classes.image} />

          {(country || flag) && (
            <span className={classes.country}>
              {flag && (
                <img
                  src={flag}
                  alt=""
                  className={classes.flag}
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              )}
              {country}
            </span>
          )}

          <button
            type="button"
            className={classes.share}
            onClick={handleShare}
            title={L.share}
            aria-label={L.share}
          >
            <FaShareAlt />
          </button>

          {expired && <span className={classes.expiredBadge}>{L.expired}</span>}
        </div>

        <div className={classes.detailBody}>
          <div className={classes.tags}>
            {audienceLabel && (
              <span className={`${classes.tag} ${classes.tagAudience}`}>{audienceLabel}</span>
            )}
            {scopeLabel && (
              <span className={`${classes.tag} ${classes.tagScope}`}>{scopeLabel}</span>
            )}
          </div>

          <h1 className={classes.detailTitle}>{title}</h1>

          <div className={classes.detailMeta}>
            <div>
              <div className={classes.metaLabel}>{L.funding}</div>
              <div className={classes.metaValue}>
                <FaDollarSign className={classes.metaIcon} />
                <span>{funding || "—"}</span>
              </div>
            </div>
            <div>
              <div className={classes.metaLabel}>{L.deadline}</div>
              <div className={`${classes.metaValue} ${grant.deadline ? classes.metaValueDeadline : ""}`}>
                <FaRegCalendarAlt className={classes.metaIcon} />
                <span>{grant.deadline ? formatDate(grant.deadline, lang) : L.noDeadline}</span>
              </div>
              {deadlineNote && (
                <div
                  className={`${classes.daysLeft} ${
                    left !== null && left >= 0 && left <= 7 ? classes.daysLeftUrgent : ""
                  }`}
                >
                  {deadlineNote}
                </div>
              )}
            </div>
            {country && (
              <div>
                <div className={classes.metaLabel}>{L.country}</div>
                <div className={classes.metaValue}>
                  {flag && (
                    <img
                      src={flag}
                      alt=""
                      className={classes.flag}
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  )}
                  <span>{country}</span>
                </div>
              </div>
            )}
          </div>

          <div
            className={classes.content}
            dangerouslySetInnerHTML={{ __html: fixHtml(content) }}
          />

          {grant.link && !expired && (
            <a
              href={grant.link}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.applyBtn}
            >
              {L.apply} <FaExternalLinkAlt />
            </a>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <>
          <h2 className={classes.relatedTitle}>{L.related}</h2>
          <div className={classes.grid}>
            {related.map((item) => (
              <GrantCard key={item.id} item={item} lang={lang} onToast={setToast} />
            ))}
          </div>
        </>
      )}

      {toast && <div className={classes.toast}>{toast}</div>}
    </div>
  );
};

export default GrantDetail;
